import { useChannelStore } from '@entities/channel'
import {
    createPartner,
    createPartnerOrd,
    deletePartner,
    editPartner,
    getPartner,
    getPartnerOrd,
    getPartners,
    getPartnersResources,
    linkPartner,
    unlinkPartner
} from '@entities/partner/api'
import { getErrorMessage } from '@shared/lib/errors'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import type { Partner } from '../types'

interface usePartnersProps {
    id?: number
    loadPartnersResources?: boolean
}

export const usePartners = ({ id, loadPartnersResources }: usePartnersProps = {}) => {
    const queryClient = useQueryClient()

    const { currentChannel } = useChannelStore(useShallow(({ current }) => ({ currentChannel: current })))

    const partnersQueryKey = ['partners', currentChannel?.id]
    const partnerQueryKey = ['partner', id, currentChannel?.id]
    const partnersResourcesQueryKey = ['partnersResources']
    const partnerOrdKey = ['partnerOrd', id]

    const partners = useQuery({
        queryKey: partnersQueryKey,
        queryFn: () => getPartners(currentChannel?.id),
        enabled: !id
    })

    const currentPartner = useQuery({
        queryKey: partnerQueryKey,
        queryFn: () => getPartner(id, currentChannel?.id),
        enabled: !!id
    })

    const partnersResources = useQuery({
        queryKey: partnersResourcesQueryKey,
        queryFn: () => getPartnersResources(),
        enabled: loadPartnersResources
    })

    const partnerOrd = useQuery({
        queryKey: partnerOrdKey,
        queryFn: () => {
            if (id) {
                return getPartnerOrd(id)
            }
        },
        enabled: !!id
    })

    const isLinkedPartner = useMemo(
        () => !!currentPartner.data?.linkedPartnerId,
        [currentPartner.data?.linkedPartnerId]
    )

    const { message } = App.useApp()

    const createPartnerMutation = useMutation({
        mutationFn: createPartner,
        onSuccess: (data) => {
            const previousChannels: Partner[] = queryClient.getQueryData(partnersQueryKey) ?? []
            const updatedChannels = [...previousChannels, data]
            queryClient.setQueryData(partnersQueryKey, updatedChannels)
            message.success('Партнер создан')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const createPartnerOrdMutation = useMutation({
        mutationFn: createPartnerOrd,
        onSuccess: () => {
            message.success('Данные сохранены')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const editPartnerMutation = useMutation({
        mutationFn: editPartner,
        onSuccess: (data) => {
            queryClient.setQueryData(['partner', data.id], data)
            message.success('Данные партнера сохранены')
        }
    })

    const deletePartnerMutation = useMutation({
        mutationFn: deletePartner,
        onSuccess: (data, variables) => {
            const previousChannels: Partner[] = queryClient.getQueryData(partnersQueryKey) ?? []
            const updatedChannels = previousChannels.filter((item) => item.id !== variables.id)
            queryClient.setQueryData(partnersQueryKey, updatedChannels)
            message.success('Партнер удален')
        }
    })

    const linkPartnerMutation = useMutation({
        mutationFn: linkPartner,
        onSuccess: () => {
            message.success('Партнер привязан')
        }
    })

    const unlinkPartnerMutation = useMutation({
        mutationFn: unlinkPartner,
        onSuccess: () => {
            currentPartner.refetch()
            message.success('Партнер успешно отвязан')
        }
    })

    return {
        partners: partners.data,
        currentPartner: currentPartner.data,
        partnerOrd: partnerOrd.data,
        isPartnersLoading: partners.isPending,
        isLinkedPartner,
        partnersResources: partnersResources.data,
        actions: {
            createPartner: createPartnerMutation.mutateAsync,
            deletePartner: deletePartnerMutation.mutateAsync,
            editPartner: editPartnerMutation.mutateAsync,
            linkPartner: linkPartnerMutation.mutateAsync,
            unlinkPartner: unlinkPartnerMutation.mutateAsync,
            refetchRartnersResources: partnersResources.refetch,
            createPartnerOrd: createPartnerOrdMutation.mutateAsync
        }
    }
}
